import React, { useState, useEffect } from 'react';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, DataTable, Spinner, Badge, FormDatePicker, FormSelect, Button, Modal, FormField, FormReactSelect, Alert } from '../ui';
import { useConfirmContext } from '../ui/feedback/ConfirmProvider';
import { History, Filter, Plus, ArrowLeftRight, Flag, X, CheckCircle, DollarSign, Users, ArrowDown, ArrowUp, Edit, Trash2 } from 'lucide-react';
import { useToastContext } from '../ui/feedback/ToastProvider';
import DirhamSymbol from '../components/DirhamSymbol';
import BulkImportModal from '../components/BulkImportModal';

const TransactionHistory = () => {
  const { user: currentUser } = useAuth();
  const { error: showError, success: showSuccess } = useToastContext();
  const { confirm } = useConfirmContext();
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [activeTab, setActiveTab] = useState('INR');
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [checkedTransactions, setCheckedTransactions] = useState(new Set());
  const [allocationData, setAllocationData] = useState(null);
  const [filters, setFilters] = useState({
    transactionType: '',
    status: '',
    startDate: '',
    endDate: '',
    minProfitAed: '',
    maxProfitAed: '',
  });
  const [inrFormData, setInrFormData] = useState({
    transactionAmountInr: '',
    date: new Date(),
    aedToUsdtRate: '',
    inrToAedRate: '',
    usdtSellingInr: ''
  });
  const [uaeFormData, setUaeFormData] = useState({
    amount: '',
    date: new Date(),
    usdtBuyingRate: '',
    usdtSellingRate: ''
  });
  const [allocationFormData, setAllocationFormData] = useState({
    recipientType: '',
    recipientId: '',
    amount: '',
    usePercentage: ''
  });
  const [totalInvestedAmount, setTotalInvestedAmount] = useState(0);

  useEffect(() => {
    fetchUsers();
    fetchBrokers();
    fetchTransactions();
    fetchTotalInvestedAmount();
  }, [filters]);

  const fetchTotalInvestedAmount = async () => {
    try {
      const response = await api.get('/api/extra-profit/total-investment');
      setTotalInvestedAmount(response.data.totalInvestment || 0);
    } catch (error) {
      console.error('Error fetching total invested amount:', error);
      // Set to 0 if error, so modal won't show incorrectly
      setTotalInvestedAmount(0);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchBrokers = async () => {
    try {
      const response = await api.get('/api/brokers');
      setBrokers(response.data.brokers || []);
    } catch (error) {
      console.error('Error fetching brokers:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.transactionType) params.transactionType = filters.transactionType;
      if (filters.status) params.status = filters.status;
      if (filters.startDate) {
        const date = filters.startDate instanceof Date
          ? filters.startDate
          : new Date(filters.startDate);
        params.startDate = date.toISOString().split('T')[0];
      }
      if (filters.endDate) {
        const date = filters.endDate instanceof Date
          ? filters.endDate
          : new Date(filters.endDate);
        params.endDate = date.toISOString().split('T')[0];
      }
      if (filters.minProfitAed) params.minProfitAed = parseFloat(filters.minProfitAed);
      if (filters.maxProfitAed) params.maxProfitAed = parseFloat(filters.maxProfitAed);

      // Use the admin endpoint to get all transactions
      const response = await api.get('/api/portfolio/all-transactions', { params });
      const transactionsData = response.data.transactions || [];

      // Map userId to user for display (userId is populated by the API)
      const transactionsWithUser = transactionsData.map(tx => ({
        ...tx,
        user: tx.userId ? {
          _id: tx.userId._id || tx.userId,
          firstName: tx.userId.firstName,
          lastName: tx.userId.lastName,
          email: tx.userId.email
        } : {
          _id: tx.userId,
          firstName: '',
          lastName: '',
          email: 'Unknown User'
        }
      }));

      setTransactions(transactionsWithUser);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      showError('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (user) => {
    if (!user) return 'Unknown User';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email || 'Unknown User';
  };

  // Calculate statistics
  const totalTransactions = transactions.length;

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return `${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  };

  const handleInrSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!inrFormData.transactionAmountInr || !inrFormData.aedToUsdtRate || !inrFormData.inrToAedRate || !inrFormData.usdtSellingInr) {
        showError('All fields are required');
        return;
      }

      const dateValue = inrFormData.date instanceof Date
        ? inrFormData.date.toISOString()
        : new Date(inrFormData.date).toISOString();

      const transactionAmountInr = parseFloat(inrFormData.transactionAmountInr);
      const aedToUsdtRate = parseFloat(inrFormData.aedToUsdtRate);

      // Convert transaction amount (USDT) to AED for comparison
      const transactionAmountAED = transactionAmountInr * aedToUsdtRate;

      // Check if transaction amount exceeds total invested amount
      const shouldShowAllocation = transactionAmountAED > totalInvestedAmount;

      const response = await api.post('/api/portfolio/create-transaction', {
        transactionType: 'INR',
        date: dateValue,
        transactionAmountInr: transactionAmountInr,
        aedToUsdtRate: aedToUsdtRate,
        inrToAedRate: parseFloat(inrFormData.inrToAedRate),
        usdtSellingInr: parseFloat(inrFormData.usdtSellingInr)
      });

      // Only show allocation modal if transaction amount > total invested amount AND there's profit
      if (shouldShowAllocation && response.data.profitAed > 0) {
        setAllocationData({
          transactionId: response.data.transaction._id,
          transactionType: 'INR',
          profitAed: response.data.profitAed,
          transaction: response.data.transaction
        });
        setShowAddModal(false);
        setShowAllocationModal(true);
      } else {
        showSuccess('Transaction created successfully');
        setShowAddModal(false);
        fetchTransactions();
      }

      // Reset form
      setInrFormData({
        transactionAmountInr: '',
        date: new Date(),
        aedToUsdtRate: '',
        inrToAedRate: '',
        usdtSellingInr: ''
      });
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to create transaction');
    }
  };

  const handleUaeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!uaeFormData.amount || !uaeFormData.usdtBuyingRate || !uaeFormData.usdtSellingRate) {
        showError('All fields are required');
        return;
      }

      const dateValue = uaeFormData.date instanceof Date
        ? uaeFormData.date.toISOString()
        : new Date(uaeFormData.date).toISOString();

      const transactionAmountAED = parseFloat(uaeFormData.amount);

      // Check if transaction amount exceeds total invested amount
      const shouldShowAllocation = transactionAmountAED > totalInvestedAmount;

      const response = await api.post('/api/portfolio/create-transaction', {
        transactionType: 'UAE',
        date: dateValue,
        amount: transactionAmountAED,
        usdtBuyingRate: parseFloat(uaeFormData.usdtBuyingRate),
        usdtSellingRate: parseFloat(uaeFormData.usdtSellingRate)
      });

      // Only show allocation modal if transaction amount > total invested amount AND there's profit
      if (shouldShowAllocation && response.data.profitAed > 0) {
        setAllocationData({
          transactionId: response.data.transaction._id,
          transactionType: 'UAE',
          profitAed: response.data.profitAed,
          transaction: response.data.transaction
        });
        setShowAddModal(false);
        setShowAllocationModal(true);
      } else {
        showSuccess('Transaction created successfully');
        setShowAddModal(false);
        fetchTransactions();
      }

      // Reset form
      setUaeFormData({
        amount: '',
        date: new Date(),
        usdtBuyingRate: '',
        usdtSellingRate: ''
      });
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to create transaction');
    }
  };

  const handleEditTransaction = (tx) => {
    setEditingTransaction(tx);
    // Set active tab based on transaction type
    if (tx.transactionType === 'INR') {
      setActiveTab('INR');
      setInrFormData({
        transactionAmountInr: tx.transactionAmountInr || '',
        date: tx.date ? new Date(tx.date) : new Date(),
        aedToUsdtRate: tx.aedToUsdtRate || '',
        inrToAedRate: tx.inrToAedRate || '',
        usdtSellingInr: tx.usdtSellingInr || ''
      });
    } else if (tx.transactionType === 'UAE') {
      setActiveTab('UAE');
      setUaeFormData({
        amount: tx.amount || tx.transactionAmountInr || '',
        date: tx.date ? new Date(tx.date) : new Date(),
        usdtBuyingRate: tx.usdtBuyingRate || '',
        usdtSellingRate: tx.usdtSellingRate || ''
      });
    } else {
      // For legacy transaction types, default to INR tab
      setActiveTab('INR');
      setInrFormData({
        transactionAmountInr: tx.amount || '',
        date: tx.date ? new Date(tx.date) : new Date(),
        aedToUsdtRate: '',
        inrToAedRate: '',
        usdtSellingInr: ''
      });
    }
    setShowEditModal(true);
  };

  const handleUpdateTransaction = async (e) => {
    e.preventDefault();
    if (!editingTransaction) return;

    try {
      const transactionType = editingTransaction.transactionType;
      let updateData = {};

      if (transactionType === 'INR') {
        if (!inrFormData.transactionAmountInr || !inrFormData.aedToUsdtRate || !inrFormData.inrToAedRate || !inrFormData.usdtSellingInr) {
          showError('All fields are required');
          return;
        }

        const dateValue = inrFormData.date instanceof Date
          ? inrFormData.date.toISOString()
          : new Date(inrFormData.date).toISOString();

        updateData = {
          transactionType: 'INR',
          date: dateValue,
          transactionAmountInr: parseFloat(inrFormData.transactionAmountInr),
          aedToUsdtRate: parseFloat(inrFormData.aedToUsdtRate),
          inrToAedRate: parseFloat(inrFormData.inrToAedRate),
          usdtSellingInr: parseFloat(inrFormData.usdtSellingInr)
        };
      } else if (transactionType === 'UAE') {
        if (!uaeFormData.amount || !uaeFormData.usdtBuyingRate || !uaeFormData.usdtSellingRate) {
          showError('All fields are required');
          return;
        }

        const dateValue = uaeFormData.date instanceof Date
          ? uaeFormData.date.toISOString()
          : new Date(uaeFormData.date).toISOString();

        updateData = {
          transactionType: 'UAE',
          date: dateValue,
          amount: parseFloat(uaeFormData.amount),
          usdtBuyingRate: parseFloat(uaeFormData.usdtBuyingRate),
          usdtSellingRate: parseFloat(uaeFormData.usdtSellingRate)
        };
      }

      const response = await api.put(`/api/portfolio/transaction/${editingTransaction._id}`, updateData);

      // If there's profit, open allocation modal to recalculate
      if (response.data.needsAllocation && response.data.profitAed > 0) {
        setAllocationData({
          transactionId: response.data.transaction._id,
          transactionType: editingTransaction.transactionType,
          profitAed: response.data.profitAed,
          transaction: response.data.transaction
        });
        setShowEditModal(false);
        setShowAllocationModal(true);
      } else {
        showSuccess('Transaction updated successfully');
        setShowEditModal(false);
        setEditingTransaction(null);
        fetchTransactions();
      }

      setEditingTransaction(null);

      // Reset forms
      setInrFormData({
        transactionAmountInr: '',
        date: new Date(),
        aedToUsdtRate: '',
        inrToAedRate: '',
        usdtSellingInr: ''
      });
      setUaeFormData({
        amount: '',
        date: new Date(),
        usdtBuyingRate: '',
        usdtSellingRate: ''
      });
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update transaction');
    }
  };

  const handleDeleteTransaction = async (tx) => {
    const confirmed = await confirm({
      title: 'Delete Transaction',
      message: `Are you sure you want to delete this ${tx.transactionType} transaction? This action cannot be undone.`,
      variant: 'danger',
      confirmText: 'Delete',
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/portfolio/transaction/${tx._id}`);
      showSuccess('Transaction deleted successfully');
      fetchTransactions();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to delete transaction');
    }
  };

  const handleBulkDeleteTransactions = async () => {
    if (checkedTransactions.size === 0) {
      showError('Please select at least one transaction to delete');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Transactions',
      message: `Are you sure you want to delete ${checkedTransactions.size} transaction(${checkedTransactions.size !== 1 ? 's' : ''})? This action cannot be undone.`,
      variant: 'danger',
      confirmText: 'Delete All',
    });

    if (!confirmed) return;

    try {
      await Promise.all(
        Array.from(checkedTransactions).map(txId =>
          api.delete(`/api/portfolio/transaction/${txId}`)
        )
      );
      showSuccess(`Deleted ${checkedTransactions.size} transaction(${checkedTransactions.size !== 1 ? 's' : ''})`);
      setCheckedTransactions(new Set());
      fetchTransactions();
    } catch (error) {
      showError('Failed to delete some transactions');
    }
  };

  const toggleTransactionCheck = (txId) => {
    const newChecked = new Set(checkedTransactions);
    if (newChecked.has(txId)) {
      newChecked.delete(txId);
    } else {
      newChecked.add(txId);
    }
    setCheckedTransactions(newChecked);
  };

  const toggleAllTransactionsCheck = (checked) => {
    if (checked) {
      setCheckedTransactions(new Set(transactions.map(t => t._id)));
    } else {
      setCheckedTransactions(new Set());
    }
  };

  /**
   * Handle extra profit allocation
   * This function allocates extra profit to investors/brokers and automatically:
   * 1. Creates/Updates ExtraProfitAllocation record
   * 2. Updates recipient balance (User/Broker)
   * 3. Creates ProfitAllocation record(s) for audit tracking
   * 4. Distributes remaining amount to admins with ProfitAllocation records
   */
  const handleAllocateProfit = async (e) => {
    e.preventDefault();

    try {
      // Validate recipient selection
      if (!allocationFormData.recipientId) {
        showError('Please select an investor or broker');
        return;
      }

      // Calculate allocation amount based on user selection
      const amount = allocationFormData.usePercentage === '50%'
        ? allocationData.profitAed * 0.5
        : allocationFormData.usePercentage === 'max'
          ? allocationData.profitAed
          : parseFloat(allocationFormData.amount) || 0;

      // Validate amount
      if (amount <= 0) {
        showError('Please enter a valid amount or select a percentage');
        return;
      }

      if (amount > allocationData.profitAed) {
        showError(`Amount cannot exceed available profit (${formatNumber(allocationData.profitAed)} AED)`);
        return;
      }

      // Call backend API to allocate profit
      // Backend will automatically:
      // - Create ExtraProfitAllocation record
      // - Update recipient (investor/broker) balance
      // - Create ProfitAllocation record for tracking and audit
      // - Distribute remaining to admins with ProfitAllocation records
      const response = await api.post('/api/portfolio/allocate-profit', {
        transactionId: allocationData.transactionId,
        recipientType: allocationFormData.recipientType,
        recipientId: allocationFormData.recipientId,
        amount: amount,
        usePercentage: allocationFormData.usePercentage
      });

      // Extract allocation details from response
      const { allocation } = response.data;

      // Calculate number of ProfitAllocation records created
      const profitAllocationCount = allocation?.allocations?.length || 0;
      const remainingAmount = allocation?.remaining_amount || 0;

      // Show detailed success message
      let successMessage = `Profit allocated successfully! `;
      successMessage += `${formatNumber(amount)} AED allocated to ${allocationFormData.recipientType}. `;

      if (remainingAmount > 0) {
        successMessage += `Remaining ${formatNumber(remainingAmount)} AED distributed to admins. `;
      }

      if (profitAllocationCount > 0) {
        successMessage += `${profitAllocationCount} allocation record(s) created.`;
      }

      showSuccess(successMessage);

      // Reset form and close modal
      setShowAllocationModal(false);
      setAllocationData(null);
      setAllocationFormData({
        recipientType: '',
        recipientId: '',
        amount: '',
        usePercentage: ''
      });

      // Refresh transactions to show updated status
      await fetchTransactions();

      // Optional: Show notification about viewing ProfitAllocation records
      console.log('📊 ProfitAllocation records created. View them at /profit-allocations');

    } catch (error) {
      console.error('Error allocating profit:', error);

      // Show detailed error message
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Failed to allocate profit. Please try again.';

      showError(errorMessage);
    }
  };

  const tableColumns = [
    {
      key: 'checkbox',
      label: (
        <input
          type="checkbox"
          checked={transactions.length > 0 && checkedTransactions.size === transactions.length}
          onChange={(e) => toggleAllTransactionsCheck(e.target.checked)}
          className="rounded"
        />
      ),
      sortable: false,
      render: (tx) => (
        <input
          type="checkbox"
          checked={checkedTransactions.has(tx._id)}
          onChange={() => toggleTransactionCheck(tx._id)}
          className="rounded"
        />
      ),
    },
    {
      key: 'date',
      label: 'DATE',
      render: (tx) => {
        const dateValue = tx.date || tx.createdAt;
        if (!dateValue) return 'N/A';
        try {
          const date = new Date(dateValue);
          return isNaN(date.getTime()) ? 'N/A' : date.toISOString().split('T')[0];
        } catch (e) {
          return 'N/A';
        }
      },
    },
    {
      key: 'aedToUsdtRate',
      label: 'AED TO USDT RATE',
      render: (tx) => formatNumber(tx.aedToUsdtRate || 0),
    },
    {
      key: 'inrToAedRate',
      label: 'INR TO AED RATE',
      render: (tx) => formatNumber(tx.inrToAedRate || 0),
    },
    {
      key: 'usdtInrCost',
      label: 'USDT INR COST',
      render: (tx) => formatNumber(tx.usdtInrCost),
    },
    {
      key: 'usdtSellingInr',
      label: 'USDT SELLING INR',
      render: (tx) => formatNumber(tx.usdtSellingInr || 0),
    },
    {
      key: 'totalUsdtSold',
      label: 'TOTAL USDT SOLD',
      render: (tx) => formatNumber(tx.totalUsdtSold || 0),
    },
    {
      key: 'aedToUsdtTotal',
      label: 'AED TO USDT TOTAL',
      render: (tx) => (
        <span className="flex items-center gap-1">
          <DirhamSymbol size={14} inline />
          {formatNumber(tx.aedToUsdtTotal)}
        </span>
      ),
    },
    {
      key: 'usdtToInrCost',
      label: 'USDT TO INR COST',
      render: (tx) => formatNumber(tx.usdtToInrCost),
    },
    {
      key: 'totalInrSold',
      label: 'TOTAL INR SOLD',
      render: (tx) => formatNumber(tx.totalInrSold),
    },
    {
      key: 'cashInHandAed',
      label: 'CASH IN HAND AED',
      render: (tx) => (
        <span className="flex items-center gap-1">
          {tx.cashInHandAed !== null && !isNaN(tx.cashInHandAed) ? (
            <>
              <DirhamSymbol size={14} inline />
              {formatNumber(tx.cashInHandAed)}
            </>
          ) : (
            '-'
          )}
        </span>
      ),
    },
    {
      key: 'profitInr',
      label: 'PROFIT INR',
      render: (tx) => (
        <span className={`font-semibold ${(tx.profitInr || 0) >= 0
          ? 'text-success-600 dark:text-success-400'
          : 'text-error-600 dark:text-error-400'
          }`}>
          {formatNumber(tx.profitInr)}
        </span>
      ),
    },
    {
      key: 'profitAed',
      label: 'PROFIT AED',
      render: (tx) => (
        <span className={`font-semibold flex items-center gap-1 ${tx.profitAed !== null && !isNaN(tx.profitAed)
          ? (tx.profitAed >= 0
            ? 'text-success-600 dark:text-success-400'
            : 'text-error-600 dark:text-error-400')
          : 'text-primary-600 dark:text-primary-400'
          }`}>
          {tx.profitAed !== null && !isNaN(tx.profitAed) ? (
            <>
              <DirhamSymbol size={14} inline />
              {formatNumber(tx.profitAed)}
            </>
          ) : (
            '-'
          )}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'STATUS',
      render: (tx) => (
        <Badge variant={tx.status === 'completed' ? 'success' : tx.status === 'pending' ? 'warning' : 'default'}>
          {tx.status || 'pending'}
        </Badge>
      ),
    },
    {
      key: 'roiPercent',
      label: 'ROI (%)',
      render: (tx) => (
        <span className={`font-semibold ${tx.roiPercent !== null && !isNaN(tx.roiPercent)
          ? (tx.roiPercent >= 0
            ? 'text-success-600 dark:text-success-400'
            : 'text-error-600 dark:text-error-400')
          : 'text-primary-600 dark:text-primary-400'
          }`}>
          {formatPercent(tx.roiPercent)}
        </span>
      ),
    },
    ...(currentUser?.role === 'admin' ? [{
      key: 'actions',
      label: 'Actions',
      render: (tx) => (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => handleEditTransaction(tx)}
            className="p-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 hover:bg-primary-100 dark:hover:bg-primary-800 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit size={18} />
          </button>
          <button
            onClick={() => handleDeleteTransaction(tx)}
            className="p-2 text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      ),
    }] : []),
  ];

  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50 flex items-center gap-2">
            <History size={28} className="text-primary-600 dark:text-primary-400" />
            Transaction History
          </h1>
          <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
            View and filter all transaction records
          </p>
        </div>
        {currentUser?.role === 'admin' && (
          <div className="flex gap-2">
            <Button onClick={() => setShowBulkImportModal(true)} variant="outline" leftIcon={<Plus size={20} />}>
              Import Transactions
            </Button>
            <Button onClick={() => setShowAddModal(true)} leftIcon={<Plus size={20} />}>
              Add Transaction
            </Button>
          </div>
        )}
      </div>


      {/* Filters */}
      <Card className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={20} className="text-primary-600 dark:text-primary-400" />
          <h3 className="font-semibold text-primary-900 dark:text-primary-50">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-primary-700 dark:text-primary-300">
              Transaction Type
            </label>
            <select
              value={filters.transactionType}
              onChange={(e) => setFilters({ ...filters, transactionType: e.target.value })}
              className="w-full px-4 py-2.5 border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Types</option>
              <option value="INR">INR</option>
              <option value="UAE">UAE</option>
              <option value="deposit">Deposit</option>
              <option value="withdrawal">Withdrawal</option>
              <option value="transfer">Transfer</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-primary-700 dark:text-primary-300">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-4 py-2.5 border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-primary-700 dark:text-primary-300">
              Start Date
            </label>
            <div>
              <FormDatePicker
                name="startDate"
                value={filters.startDate}
                onChange={(e) => {
                  if (e.target.value) {
                    const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (dateMatch) {
                      const year = parseInt(dateMatch[1], 10);
                      const month = parseInt(dateMatch[2], 10) - 1;
                      const day = parseInt(dateMatch[3], 10);
                      const dateValue = new Date(year, month, day);
                      setFilters({ ...filters, startDate: dateValue });
                    } else {
                      setFilters({ ...filters, startDate: null });
                    }
                  } else {
                    setFilters({ ...filters, startDate: null });
                  }
                }}
                placeholderText="Select start date"
                className="space-y-0"
                label={null}
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-primary-700 dark:text-primary-300">
              End Date
            </label>
            <div>
              <FormDatePicker
                name="endDate"
                value={filters.endDate}
                onChange={(e) => {
                  if (e.target.value) {
                    const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (dateMatch) {
                      const year = parseInt(dateMatch[1], 10);
                      const month = parseInt(dateMatch[2], 10) - 1;
                      const day = parseInt(dateMatch[3], 10);
                      const dateValue = new Date(year, month, day);
                      setFilters({ ...filters, endDate: dateValue });
                    } else {
                      setFilters({ ...filters, endDate: null });
                    }
                  } else {
                    setFilters({ ...filters, endDate: null });
                  }
                }}
                placeholderText="Select end date"
                className="space-y-0"
                label={null}
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-primary-700 dark:text-primary-300">
              Min Profit AED
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                value={filters.minProfitAed}
                onChange={(e) => setFilters({ ...filters, minProfitAed: e.target.value })}
                placeholder="0.00"
                className="w-full px-4 py-2.5 pr-10 border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <DirhamSymbol size={16} className="text-primary-500 dark:text-primary-400" />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-primary-700 dark:text-primary-300">
              Max Profit AED
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                value={filters.maxProfitAed}
                onChange={(e) => setFilters({ ...filters, maxProfitAed: e.target.value })}
                placeholder="0.00"
                className="w-full px-4 py-2.5 pr-10 border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <DirhamSymbol size={16} className="text-primary-500 dark:text-primary-400" />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Transactions Table */}
      <Card className="p-6">
        {checkedTransactions.size > 0 && (
          <div className="mb-4 p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg flex items-center justify-between">
            <span className="text-error-700 dark:text-error-400 font-medium">
              {checkedTransactions.size} transaction(s) selected
            </span>
            <Button
              onClick={handleBulkDeleteTransactions}
              variant="ghost"
              size="sm"
              className="text-error-600 dark:text-error-400 hover:bg-error-100 dark:hover:bg-error-900/30"
              leftIcon={<Trash2 size={18} />}
            >
              Delete Selected
            </Button>
          </div>
        )}
        {transactions.length > 0 ? (
          <DataTable
            columns={tableColumns}
            data={transactions}
            striped
            hover
            exportFileName="transactions"
            searchableFields={['investor_name', 'transactionType', 'description']}
          />
        ) : (
          <div className="text-center py-12">
            <History size={48} className="mx-auto text-primary-300 dark:text-primary-600 mb-4" />
            <p className="text-primary-600 dark:text-primary-400">No transactions found</p>
          </div>
        )}
      </Card>

      {/* Add Transaction Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setActiveTab('INR');
          setInrFormData({
            transactionAmountInr: '',
            date: new Date(),
            aedToUsdtRate: '',
            inrToAedRate: '',
            usdtSellingInr: ''
          });
          setUaeFormData({
            amount: '',
            date: new Date(),
            usdtBuyingRate: '',
            usdtSellingRate: ''
          });
        }}
        title={activeTab === 'INR' ? 'Add INR Transaction' : 'Add UAE Transaction'}
        size="lg"
      >
        <div className="space-y-4">
          {/* Tabs */}
          <div className="flex gap-2 border-b border-primary-200 dark:border-primary-700">
            <button
              type="button"
              onClick={() => setActiveTab('INR')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'INR'
                ? 'border-b-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                : 'text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
                }`}
            >
              <div className="flex items-center justify-center gap-2">
                <ArrowLeftRight size={18} />
                INR
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('UAE')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'UAE'
                ? 'border-b-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                : 'text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
                }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Flag size={18} />
                UAE
              </div>
            </button>
          </div>

          {/* INR Form */}
          {activeTab === 'INR' && (
            <form onSubmit={handleInrSubmit} className="space-y-4">
              <p className="text-sm text-primary-600 dark:text-primary-400 mb-4">Enter transaction details.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Transaction Amount (USDT)"
                  name="transactionAmountInr"
                  type="number"
                  step="0.01"
                  required
                  value={inrFormData.transactionAmountInr}
                  onChange={(e) => setInrFormData({ ...inrFormData, transactionAmountInr: e.target.value })}
                  placeholder="0.00"
                />
                <FormDatePicker
                  label="Transaction Date"
                  name="date"
                  required
                  value={inrFormData.date}
                  onChange={(e) => {
                    if (e.target.value) {
                      const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                      if (dateMatch) {
                        const year = parseInt(dateMatch[1], 10);
                        const month = parseInt(dateMatch[2], 10) - 1;
                        const day = parseInt(dateMatch[3], 10);
                        const dateValue = new Date(year, month, day);
                        setInrFormData({ ...inrFormData, date: dateValue });
                      }
                    }
                  }}
                  placeholderText="dd/mm/yyyy"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                    AED to USDT Rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.0001"
                      required
                      value={inrFormData.aedToUsdtRate}
                      onChange={(e) => setInrFormData({ ...inrFormData, aedToUsdtRate: e.target.value })}
                      placeholder="0.0000"
                      className="w-full px-4 py-2.5 pl-10 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                    <ArrowLeftRight size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                    INR to AED Rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.0001"
                      required
                      value={inrFormData.inrToAedRate}
                      onChange={(e) => setInrFormData({ ...inrFormData, inrToAedRate: e.target.value })}
                      placeholder="0.0000"
                      className="w-full px-4 py-2.5 pl-10 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                    <ArrowLeftRight size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                  </div>
                </div>
              </div>
              <FormField
                label="USDT Selling INR"
                name="usdtSellingInr"
                type="number"
                step="0.0001"
                required
                value={inrFormData.usdtSellingInr}
                onChange={(e) => setInrFormData({ ...inrFormData, usdtSellingInr: e.target.value })}
                placeholder="0.0000"
              />
              <div className="flex gap-3 pt-4">
                <Button type="submit" variant="primary" className="flex-1" leftIcon={<ArrowLeftRight size={18} />}>
                  Add Transaction
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddModal(false);
                    setInrFormData({
                      transactionAmountInr: '',
                      date: new Date(),
                      aedToUsdtRate: '',
                      inrToAedRate: '',
                      usdtSellingInr: ''
                    });
                  }}
                  className="flex-1"
                  leftIcon={<X size={18} />}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {/* UAE Form */}
          {activeTab === 'UAE' && (
            <form onSubmit={handleUaeSubmit} className="space-y-4">
              <p className="text-sm text-primary-600 dark:text-primary-400 mb-4">Enter transaction details.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Transaction Amount"
                  name="amount"
                  type="number"
                  step="0.01"
                  required
                  value={uaeFormData.amount}
                  onChange={(e) => setUaeFormData({ ...uaeFormData, amount: e.target.value })}
                  placeholder="0.00"
                />
                <FormDatePicker
                  label="Transaction Date"
                  name="date"
                  required
                  value={uaeFormData.date}
                  onChange={(e) => {
                    if (e.target.value) {
                      const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                      if (dateMatch) {
                        const year = parseInt(dateMatch[1], 10);
                        const month = parseInt(dateMatch[2], 10) - 1;
                        const day = parseInt(dateMatch[3], 10);
                        const dateValue = new Date(year, month, day);
                        setUaeFormData({ ...uaeFormData, date: dateValue });
                      }
                    }
                  }}
                  placeholderText="dd/mm/yyyy"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                    USDT Buying Rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.0001"
                      required
                      value={uaeFormData.usdtBuyingRate}
                      onChange={(e) => setUaeFormData({ ...uaeFormData, usdtBuyingRate: e.target.value })}
                      placeholder="0.0000"
                      className="w-full px-4 py-2.5 pl-10 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                    <ArrowDown size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                    USDT Selling Rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.0001"
                      required
                      value={uaeFormData.usdtSellingRate}
                      onChange={(e) => setUaeFormData({ ...uaeFormData, usdtSellingRate: e.target.value })}
                      placeholder="0.0000"
                      className="w-full px-4 py-2.5 pl-10 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                    <ArrowUp size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" variant="primary" className="flex-1" leftIcon={<Flag size={18} />}>
                  Add Transaction
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddModal(false);
                    setUaeFormData({
                      amount: '',
                      date: new Date(),
                      usdtBuyingRate: '',
                      usdtSellingRate: ''
                    });
                  }}
                  className="flex-1"
                  leftIcon={<X size={18} />}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      {/* Edit Transaction Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingTransaction(null);
          setActiveTab('INR');
          setInrFormData({
            transactionAmountInr: '',
            date: new Date(),
            aedToUsdtRate: '',
            inrToAedRate: '',
            usdtSellingInr: ''
          });
          setUaeFormData({
            amount: '',
            date: new Date(),
            usdtBuyingRate: '',
            usdtSellingRate: ''
          });
        }}
        title={activeTab === 'INR' ? 'Edit INR Transaction' : 'Edit UAE Transaction'}
        size="lg"
      >
        {editingTransaction && (
          <div className="space-y-4">
            {/* Tabs */}
            <div className="flex gap-2 border-b border-primary-200 dark:border-primary-700">
              <button
                type="button"
                onClick={() => {
                  // Only allow switching if transaction type matches or is legacy
                  if (editingTransaction.transactionType === 'INR' || !['INR', 'UAE'].includes(editingTransaction.transactionType)) {
                    setActiveTab('INR');
                  }
                }}
                disabled={editingTransaction.transactionType === 'UAE'}
                className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'INR'
                  ? 'border-b-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                  : 'text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
                  } ${editingTransaction.transactionType === 'UAE' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center justify-center gap-2">
                  <ArrowLeftRight size={18} />
                  INR
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  // Only allow switching if transaction type matches
                  if (editingTransaction.transactionType === 'UAE') {
                    setActiveTab('UAE');
                  }
                }}
                disabled={editingTransaction.transactionType === 'INR' || !['INR', 'UAE'].includes(editingTransaction.transactionType)}
                className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'UAE'
                  ? 'border-b-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                  : 'text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
                  } ${editingTransaction.transactionType === 'INR' || !['INR', 'UAE'].includes(editingTransaction.transactionType) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Flag size={18} />
                  UAE
                </div>
              </button>
            </div>

            {/* INR Form */}
            {activeTab === 'INR' && (
              <form onSubmit={handleUpdateTransaction} className="space-y-4">
                <p className="text-sm text-primary-600 dark:text-primary-400 mb-4">Enter transaction details.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="Transaction Amount (USDT)"
                    name="transactionAmountInr"
                    type="number"
                    step="0.01"
                    required
                    value={inrFormData.transactionAmountInr}
                    onChange={(e) => setInrFormData({ ...inrFormData, transactionAmountInr: e.target.value })}
                    placeholder="0.00"
                  />
                  <FormDatePicker
                    label="Transaction Date"
                    name="date"
                    required
                    value={inrFormData.date}
                    onChange={(e) => {
                      if (e.target.value) {
                        const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                        if (dateMatch) {
                          const year = parseInt(dateMatch[1], 10);
                          const month = parseInt(dateMatch[2], 10) - 1;
                          const day = parseInt(dateMatch[3], 10);
                          const dateValue = new Date(year, month, day);
                          setInrFormData({ ...inrFormData, date: dateValue });
                        }
                      }
                    }}
                    placeholderText="dd/mm/yyyy"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                      AED to USDT Rate
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.0001"
                        required
                        value={inrFormData.aedToUsdtRate}
                        onChange={(e) => setInrFormData({ ...inrFormData, aedToUsdtRate: e.target.value })}
                        placeholder="0.0000"
                        className="w-full px-4 py-2.5 pl-10 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                      />
                      <ArrowLeftRight size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                      INR to AED Rate
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.0001"
                        required
                        value={inrFormData.inrToAedRate}
                        onChange={(e) => setInrFormData({ ...inrFormData, inrToAedRate: e.target.value })}
                        placeholder="0.0000"
                        className="w-full px-4 py-2.5 pl-10 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                      />
                      <ArrowLeftRight size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                    </div>
                  </div>
                </div>
                <FormField
                  label="USDT Selling INR"
                  name="usdtSellingInr"
                  type="number"
                  step="0.0001"
                  required
                  value={inrFormData.usdtSellingInr}
                  onChange={(e) => setInrFormData({ ...inrFormData, usdtSellingInr: e.target.value })}
                  placeholder="0.0000"
                />
                <div className="flex gap-3 pt-4">
                  <Button type="submit" variant="primary" className="flex-1" leftIcon={<ArrowLeftRight size={18} />}>
                    Update Transaction
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingTransaction(null);
                    }}
                    className="flex-1"
                    leftIcon={<X size={18} />}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* UAE Form */}
            {activeTab === 'UAE' && (
              <form onSubmit={handleUpdateTransaction} className="space-y-4">
                <p className="text-sm text-primary-600 dark:text-primary-400 mb-4">Enter transaction details.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="Transaction Amount"
                    name="amount"
                    type="number"
                    step="0.01"
                    required
                    value={uaeFormData.amount}
                    onChange={(e) => setUaeFormData({ ...uaeFormData, amount: e.target.value })}
                    placeholder="0.00"
                  />
                  <FormDatePicker
                    label="Transaction Date"
                    name="date"
                    required
                    value={uaeFormData.date}
                    onChange={(e) => {
                      if (e.target.value) {
                        const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                        if (dateMatch) {
                          const year = parseInt(dateMatch[1], 10);
                          const month = parseInt(dateMatch[2], 10) - 1;
                          const day = parseInt(dateMatch[3], 10);
                          const dateValue = new Date(year, month, day);
                          setUaeFormData({ ...uaeFormData, date: dateValue });
                        }
                      }
                    }}
                    placeholderText="dd/mm/yyyy"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                      USDT Buying Rate
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.0001"
                        required
                        value={uaeFormData.usdtBuyingRate}
                        onChange={(e) => setUaeFormData({ ...uaeFormData, usdtBuyingRate: e.target.value })}
                        placeholder="0.0000"
                        className="w-full px-4 py-2.5 pl-10 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                      />
                      <ArrowDown size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                      USDT Selling Rate
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.0001"
                        required
                        value={uaeFormData.usdtSellingRate}
                        onChange={(e) => setUaeFormData({ ...uaeFormData, usdtSellingRate: e.target.value })}
                        placeholder="0.0000"
                        className="w-full px-4 py-2.5 pl-10 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                      />
                      <ArrowUp size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" variant="primary" className="flex-1" leftIcon={<Flag size={18} />}>
                    Update Transaction
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingTransaction(null);
                    }}
                    className="flex-1"
                    leftIcon={<X size={18} />}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}
      </Modal>

      {/* Extra Profit Allocation Modal */}
      <Modal
        isOpen={showAllocationModal}
        onClose={() => {
          setShowAllocationModal(false);
          setAllocationData(null);
          setAllocationFormData({
            recipientType: '',
            recipientId: '',
            amount: '',
            usePercentage: ''
          });
        }}
        title="Extra Profit Allocation"
        size="lg"
      >
        {allocationData && (
          <form onSubmit={handleAllocateProfit} className="space-y-6">
            <p className="text-sm text-primary-600 dark:text-primary-400">Distribute the extra profit.</p>

            {/* Info Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="p-4 bg-primary-50 dark:bg-primary-900/50 border border-primary-200 dark:border-primary-700">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowLeftRight size={20} className="text-primary-600 dark:text-primary-400" />
                  <span className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase">Transaction</span>
                </div>
                <p className="text-lg font-bold text-primary-900 dark:text-primary-50">{allocationData.transactionType}</p>
              </Card>
              <Card className="p-4 bg-secondary-50 dark:bg-secondary-900/50 border border-secondary-200 dark:border-secondary-700">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={20} className="text-secondary-600 dark:text-secondary-400" />
                  <span className="text-xs font-medium text-secondary-600 dark:text-secondary-400 uppercase">Extra Amount</span>
                </div>
                <p className="text-lg font-bold text-secondary-900 dark:text-secondary-50 flex items-center gap-1">
                  <span className="text-sm">USDT</span>
                </p>
              </Card>
              <Card className="p-4 bg-success-50 dark:bg-success-900/50 border border-success-200 dark:border-success-700">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign size={20} className="text-success-600 dark:text-success-400" />
                  <span className="text-xs font-medium text-success-600 dark:text-success-400 uppercase">Extra Profit</span>
                </div>
                <p className="text-lg font-bold text-success-900 dark:text-success-50 flex items-center gap-1">
                  <DirhamSymbol size={16} inline />
                  {formatNumber(allocationData.profitAed)}
                  <span className="text-sm ml-1">AED</span>
                </p>
              </Card>
            </div>

            <Alert variant="warning">
              <div className="flex items-start gap-2">
                <div className="w-1 h-1 rounded-full bg-warning-600 dark:bg-warning-400 mt-1.5" />
                <div>
                  <p className="font-semibold text-warning-900 dark:text-warning-50">Allocation Required</p>
                  <p className="text-sm text-warning-700 dark:text-warning-300 mt-1">
                    The transaction amount exceeded the initial investment limit. Allocate extra profit to an investor/broker. The remaining amount will be automatically assigned to Admin.
                  </p>
                </div>
              </div>
            </Alert>

            <div>
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                <Users size={18} className="inline mr-2" />
                Select Investor or Broker
              </label>
              <FormReactSelect
                name="recipientId"
                required
                value={allocationFormData.recipientId}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedUser = users.find(u => u._id === selectedId);
                  const selectedBroker = brokers.find(b => b._id === selectedId);
                  setAllocationFormData({
                    ...allocationFormData,
                    recipientId: selectedId,
                    recipientType: selectedUser ? 'investor' : selectedBroker ? 'broker' : ''
                  });
                }}
                options={[
                  ...users.filter(u => u.role === 'investor').map(u => ({
                    value: u._id,
                    label: `${u.firstName} ${u.lastName} (${u.email})`
                  })),
                  ...brokers.map(b => ({
                    value: b._id,
                    label: `${b.name} (${b.email})`
                  }))
                ]}
                placeholder="-- Select Investor or Broker --"
              />
              <p className="text-xs text-primary-500 dark:text-primary-400 mt-1">
                {users.filter(u => u.role === 'investor').length} Investors, {brokers.length} Brokers available
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                <DollarSign size={18} className="inline mr-2" />
                Profit Share Amount (AED)
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={allocationFormData.amount}
                  onChange={(e) => setAllocationFormData({ ...allocationFormData, amount: e.target.value, usePercentage: '' })}
                  placeholder="0"
                  className="w-full px-4 py-2.5 pr-16 rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-500 dark:text-primary-400">AED</span>
              </div>
              <p className="text-xs text-primary-500 dark:text-primary-400 mt-1">
                Maximum: {formatNumber(allocationData.profitAed)} AED
              </p>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={allocationFormData.usePercentage === '50%' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    const amount = allocationData.profitAed * 0.5;
                    setAllocationFormData({
                      ...allocationFormData,
                      usePercentage: '50%',
                      amount: amount.toFixed(2)
                    });
                  }}
                >
                  50%
                </Button>
                <Button
                  type="button"
                  variant={allocationFormData.usePercentage === 'max' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setAllocationFormData({
                      ...allocationFormData,
                      usePercentage: 'max',
                      amount: allocationData.profitAed.toFixed(2)
                    });
                  }}
                >
                  Max
                </Button>
              </div>
            </div>

            {/* Allocation Summary */}
            <div className="border-t border-primary-200 dark:border-primary-700 pt-4">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle size={18} className="text-primary-600 dark:text-primary-400" />
                <h3 className="font-semibold text-primary-900 dark:text-primary-50">Allocation Summary</h3>
              </div>
              <div className="mb-4">
                <div className="flex justify-between text-xs text-primary-600 dark:text-primary-400 mb-1">
                  <span>Allocation Progress</span>
                  <span>
                    {allocationFormData.amount
                      ? `${((parseFloat(allocationFormData.amount) / allocationData.profitAed) * 100).toFixed(1)}%`
                      : '0.0%'}
                  </span>
                </div>
                <div className="w-full bg-primary-200 dark:bg-primary-700 rounded-full h-2">
                  <div
                    className="bg-primary-600 dark:bg-primary-400 h-2 rounded-full transition-all"
                    style={{
                      width: allocationFormData.amount
                        ? `${Math.min((parseFloat(allocationFormData.amount) / allocationData.profitAed) * 100, 100)}%`
                        : '0%'
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-3 bg-success-50 dark:bg-success-900/50 border border-success-200 dark:border-success-700">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-success-600 dark:bg-success-400" />
                    <span className="text-xs font-medium text-success-600 dark:text-success-400">Allocated to User</span>
                  </div>
                  <p className="text-lg font-bold text-success-900 dark:text-success-50 flex items-center gap-1">
                    <DirhamSymbol size={14} inline />
                    {formatNumber(parseFloat(allocationFormData.amount) || 0)}
                  </p>
                  <p className="text-xs text-success-600 dark:text-success-400 mt-1">
                    {allocationFormData.amount
                      ? `${((parseFloat(allocationFormData.amount) / allocationData.profitAed) * 100).toFixed(1)}% of total`
                      : '0.0% of total'}
                  </p>
                </Card>
                <Card className="p-3 bg-primary-50 dark:bg-primary-900/50 border border-primary-200 dark:border-primary-700">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-primary-600 dark:bg-primary-400" />
                    <span className="text-xs font-medium text-primary-600 dark:text-primary-400">Remaining for Admin</span>
                  </div>
                  <p className="text-lg font-bold text-primary-900 dark:text-primary-50 flex items-center gap-1">
                    <DirhamSymbol size={14} inline />
                    {formatNumber(Math.max(0, allocationData.profitAed - (parseFloat(allocationFormData.amount) || 0)))}
                  </p>
                  <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                    {allocationFormData.amount
                      ? `${(((allocationData.profitAed - parseFloat(allocationFormData.amount)) / allocationData.profitAed) * 100).toFixed(1)}% of total`
                      : '100.0% of total'}
                  </p>
                </Card>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAllocationModal(false);
                  setAllocationData(null);
                  setAllocationFormData({
                    recipientType: '',
                    recipientId: '',
                    amount: '',
                    usePercentage: ''
                  });
                }}
                className="flex-1"
                leftIcon={<X size={18} />}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="flex-1"
                leftIcon={<CheckCircle size={18} />}
              >
                Allocate Profit
              </Button>
            </div>
          </form>
        )}
      </Modal>
      <BulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onSuccess={() => {
          setShowBulkImportModal(false);
          fetchTransactions();
        }}
      />
      <BulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onSuccess={() => {
          setShowBulkImportModal(false);
          fetchTransactions();
        }}
      />
    </div>
  );
};

export default TransactionHistory;
